import { sql } from "drizzle-orm";
import {
  pgTable,
  uuid,
  text,
  varchar,
  timestamp,
  jsonb,
} from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports (relative — NEVER use @shared/ alias, it breaks esbuild)
import { organisations } from "./schema-security.js";
import { contacts } from "./schema-crm.js";
import { accounts } from "./schema-crm.js";
import { deals } from "./schema-deals.js";
import { clientGroups } from "./schema-crm.js";

// ============================================================================
// ACTIVITY INFRASTRUCTURE MODULE (Sprint A — EDG-319)
// Polymorphic audit log, notes, note revisions, and mentions.
// All tables use entity_type TEXT + entity_id UUID pattern.
// Full RLS from day one per Sprint 27 pattern.
// ============================================================================

// ============================================================================
// TABLE 1: crm_x_entity_audit_log
// Append-only polymorphic audit log scoped to an organisation.
// ============================================================================

export const entityAuditLog = pgTable("crm_x_entity_audit_log", {
  id:                uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId:    uuid("organisation_id").notNull().references(() => organisations.id),
  entityType:        text("entity_type").notNull(),         // 'contact' | 'account' | 'deal' | 'client_group' | etc.
  entityId:          uuid("entity_id").notNull(),
  relatedEntityType: text("related_entity_type"),
  relatedEntityId:   uuid("related_entity_id"),
  action:            text("action").notNull(),              // 'INSERT' | 'UPDATE' | 'DELETE' | custom verb
  performedBy:       text("performed_by"),                  // Clerk user ID (TEXT per migration 0405 pattern)
  performedByName:   text("performed_by_name"),
  performedAt:       timestamp("performed_at", { withTimezone: true }).notNull().default(sql`now()`),
  renderedText:      text("rendered_text"),                 // pre-rendered prose from formatter
  rawData:           jsonb("raw_data"),                     // source mutation payload
});

// ============================================================================
// TABLE 2: crm_x_entity_notes
// Compliance-grade notes with soft-delete.
// Exactly one of {contact_id, account_id, deal_id, client_group_id} is set
// (enforced in DB by entity_notes_one_parent CHECK constraint).
// ============================================================================

export const entityNotes = pgTable("crm_x_entity_notes", {
  id:            uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId:      uuid("tenant_id").notNull().references(() => organisations.id),
  // Polymorphic parent — exactly one NOT NULL (DB CHECK constraint enforces this)
  contactId:     uuid("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  accountId:     uuid("account_id").references(() => accounts.id, { onDelete: "cascade" }),
  dealId:        uuid("deal_id").references(() => deals.id, { onDelete: "cascade" }),
  clientGroupId: uuid("client_group_id").references(() => clientGroups.id, { onDelete: "cascade" }),
  // Content
  body:          text("body").notNull(),
  // Authorship + lifecycle
  authorUserId:  text("author_user_id").notNull(),          // Clerk user ID (TEXT per migration 0405 pattern)
  createdAt:     timestamp("created_at", { withTimezone: true }).notNull().default(sql`now()`),
  updatedAt:     timestamp("updated_at", { withTimezone: true }).notNull().default(sql`now()`),
  deletedAt:     timestamp("deleted_at", { withTimezone: true }),
});

// ============================================================================
// TABLE 3: crm_x_entity_note_revisions
// Edit history — auto-populated by DB trigger on entity_notes UPDATE.
// ============================================================================

export const entityNoteRevisions = pgTable("crm_x_entity_note_revisions", {
  id:              uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  noteId:          uuid("note_id").notNull().references(() => entityNotes.id, { onDelete: "cascade" }),
  body:            text("body").notNull(),
  editedByUserId:  text("edited_by_user_id").notNull(),
  editedAt:        timestamp("edited_at", { withTimezone: true }).notNull().default(sql`now()`),
});

// ============================================================================
// TABLE 4: crm_x_entity_mentions
// Polymorphic mentions — linked to a note and an entity context.
// ============================================================================

export const entityMentions = pgTable("crm_x_entity_mentions", {
  id:                 uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  entityType:         text("entity_type").notNull(),
  entityId:           uuid("entity_id").notNull(),
  noteId:             uuid("note_id").references(() => entityNotes.id, { onDelete: "cascade" }),
  mentionedUserId:    text("mentioned_user_id").notNull(),
  mentionedUserName:  varchar("mentioned_user_name", { length: 255 }).notNull(),
  notifiedAt:         timestamp("notified_at", { withTimezone: true }),
  createdAt:          timestamp("created_at", { withTimezone: true }).notNull().default(sql`now()`),
});

// ============================================================================
// RELATIONS
// ============================================================================

export const entityAuditLogRelations = relations(entityAuditLog, ({ one }) => ({
  organisation: one(organisations, {
    fields: [entityAuditLog.organisationId],
    references: [organisations.id],
  }),
}));

export const entityNotesRelations = relations(entityNotes, ({ one, many }) => ({
  tenant: one(organisations, {
    fields: [entityNotes.tenantId],
    references: [organisations.id],
  }),
  contact: one(contacts, {
    fields: [entityNotes.contactId],
    references: [contacts.id],
  }),
  account: one(accounts, {
    fields: [entityNotes.accountId],
    references: [accounts.id],
  }),
  deal: one(deals, {
    fields: [entityNotes.dealId],
    references: [deals.id],
  }),
  clientGroup: one(clientGroups, {
    fields: [entityNotes.clientGroupId],
    references: [clientGroups.id],
  }),
  revisions: many(entityNoteRevisions),
  mentions: many(entityMentions),
}));

export const entityNoteRevisionsRelations = relations(entityNoteRevisions, ({ one }) => ({
  note: one(entityNotes, {
    fields: [entityNoteRevisions.noteId],
    references: [entityNotes.id],
  }),
}));

export const entityMentionsRelations = relations(entityMentions, ({ one }) => ({
  note: one(entityNotes, {
    fields: [entityMentions.noteId],
    references: [entityNotes.id],
  }),
}));

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const insertEntityNoteSchema = createInsertSchema(entityNotes, {
  tenantId:     z.string().uuid(),
  contactId:    z.string().uuid().optional().nullable(),
  accountId:    z.string().uuid().optional().nullable(),
  dealId:       z.string().uuid().optional().nullable(),
  clientGroupId: z.string().uuid().optional().nullable(),
  body:         z.string().min(1, "Note body cannot be empty"),
  authorUserId: z.string().min(1, "Author user ID is required"),
});

export const selectEntityNoteSchema = createSelectSchema(entityNotes);

export const insertEntityMentionSchema = createInsertSchema(entityMentions, {
  entityType:        z.string().min(1),
  entityId:          z.string().uuid(),
  noteId:            z.string().uuid().optional().nullable(),
  mentionedUserId:   z.string().min(1),
  mentionedUserName: z.string().min(1).max(255),
});

export const selectEntityMentionSchema = createSelectSchema(entityMentions);

export const insertEntityAuditLogSchema = createInsertSchema(entityAuditLog, {
  organisationId:    z.string().uuid(),
  entityType:        z.string().min(1),
  entityId:          z.string().uuid(),
  action:            z.string().min(1),
  performedBy:       z.string().optional().nullable(),
  performedByName:   z.string().optional().nullable(),
  renderedText:      z.string().optional().nullable(),
  rawData:           z.any().optional().nullable(),
});

// TypeScript types
export type EntityAuditLog = typeof entityAuditLog.$inferSelect;
export type InsertEntityAuditLog = typeof entityAuditLog.$inferInsert;
export type EntityNote = typeof entityNotes.$inferSelect;
export type InsertEntityNote = typeof entityNotes.$inferInsert;
export type EntityNoteRevision = typeof entityNoteRevisions.$inferSelect;
export type InsertEntityNoteRevision = typeof entityNoteRevisions.$inferInsert;
export type EntityMention = typeof entityMentions.$inferSelect;
export type InsertEntityMention = typeof entityMentions.$inferInsert;
